import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarClock,
  MapPin,
  FileText,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Filter,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EVENT_STYLES = {
  einde_contract: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700 border-red-200' },
  opzegmoment: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  indexatie: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
  compliance_deadline: { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700 border-purple-200' },
};

const EVENT_LABELS = {
  einde_contract: 'Einde contract',
  opzegmoment: 'Opzegmoment',
  indexatie: 'Indexatie',
  compliance_deadline: 'Compliance deadline',
};

const URGENCY_RING = {
  red: 'ring-2 ring-red-300',
  amber: 'ring-2 ring-amber-200',
  blue: '',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

function getQuarterKey(dateStr) {
  try {
    const d = new Date(dateStr);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    return `${d.getFullYear()}-Q${q}`;
  } catch { return 'Onbekend'; }
}

function getQuarterLabel(key) {
  const [year, q] = key.split('-');
  return `${q} ${year}`;
}

export default function ContractCalendar({ locations }) {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLocation, setFilterLocation] = useState('all');
  const [collapsedQuarters, setCollapsedQuarters] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const api = authAxios();
        const params = filterLocation !== 'all' ? `?location_id=${filterLocation}` : '';
        const res = await api.get(`/contracts/calendar${params}`);
        setEvents(res.data.events || []);
      } catch (e) {
        console.error('Contract calendar load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authAxios, filterLocation]);

  const toggleQuarter = (key) => {
    setCollapsedQuarters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Group events by quarter
  const grouped = {};
  for (const ev of events) {
    const key = getQuarterKey(ev.date);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  }
  const quarters = Object.keys(grouped).sort();

  const locationMap = {};
  (locations || []).forEach(l => { locationMap[l.location_id] = l.name; });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 text-[#2E75B6] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contract-calendar">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Filter className="w-3.5 h-3.5" />
        </div>
        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="h-9 w-48 text-xs" data-testid="calendar-filter-location">
            <SelectValue placeholder="Alle locaties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle locaties</SelectItem>
            {(locations || []).filter(l => l.status === 'active').map(loc => (
              <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400 ml-auto">{events.length} events</span>
      </div>

      {events.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200/80 p-12 text-center">
          <CalendarClock className="w-8 h-8 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Geen contractgebeurtenissen gevonden</p>
          <p className="text-xs text-slate-400 mt-1">Upload contracten met AI Normalisatie om datums te extraheren</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quarters.map(qKey => {
            const qEvents = grouped[qKey];
            const collapsed = collapsedQuarters[qKey];
            const hasUrgent = qEvents.some(e => e.urgency === 'red');

            return (
              <div key={qKey} className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
                <button
                  onClick={() => toggleQuarter(qKey)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`quarter-${qKey}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-sm font-semibold text-slate-800">{getQuarterLabel(qKey)}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
                      {qEvents.length}
                    </span>
                    {hasUrgent && (
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                    )}
                  </div>
                  {collapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                </button>

                {!collapsed && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {qEvents.map((ev, idx) => {
                      const style = EVENT_STYLES[ev.type] || EVENT_STYLES.compliance_deadline;
                      const ringClass = URGENCY_RING[ev.urgency] || '';

                      return (
                        <div
                          key={`${ev.entry_id || ev.compliance_id}-${idx}`}
                          data-testid={`calendar-event-${idx}`}
                          onClick={() => {
                            if (ev.entry_id) navigate(`/kennisbank/${ev.entry_id}`);
                          }}
                          className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 cursor-pointer transition-colors ${ringClass}`}
                        >
                          {/* Date column */}
                          <div className="w-16 text-center flex-shrink-0">
                            <div className="text-lg font-bold text-slate-800 leading-none">
                              {new Date(ev.date).getDate()}
                            </div>
                            <div className="text-[10px] uppercase text-slate-400 mt-0.5">
                              {new Date(ev.date).toLocaleDateString('nl-NL', { month: 'short' })}
                            </div>
                          </div>

                          {/* Vertical line */}
                          <div className={`w-0.5 h-10 rounded-full ${ev.urgency === 'red' ? 'bg-red-400' : ev.urgency === 'amber' ? 'bg-amber-400' : 'bg-blue-300'}`} />

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${style.badge}`}>
                                {EVENT_LABELS[ev.type] || ev.type}
                              </span>
                              {ev.days_until !== null && ev.days_until <= 90 && (
                                <span className="text-[10px] text-red-600 font-medium">
                                  {ev.days_until < 0 ? `${Math.abs(ev.days_until)}d verlopen` : ev.days_until === 0 ? 'Vandaag' : `nog ${ev.days_until}d`}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-800 font-medium truncate">{ev.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {ev.location_id && ev.location_id !== 'all' && (
                                <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                                  <MapPin className="w-2.5 h-2.5" />
                                  {locationMap[ev.location_id] || ev.location_id}
                                </span>
                              )}
                              <span className="text-[10px] text-slate-400">{formatDate(ev.date)}</span>
                            </div>
                          </div>

                          {/* Source icon */}
                          <div className="flex-shrink-0">
                            {ev.source === 'key_data' ? (
                              <FileText className="w-4 h-4 text-slate-300" />
                            ) : (
                              <CalendarClock className="w-4 h-4 text-slate-300" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
